import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { ArrowRight, ShoppingCart } from "lucide-react";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("Todos");
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const cats = ["Todos", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = category === "Todos" ? products : products.filter((p) => p.category === category);
  return (
    <div className="anim-in">
      <div className="border-b border-border">
        <div className="container-x py-16">
          <p className="tech-label mb-3">Loja · Produtos cadditive</p>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter">Produtos próprios.</h1>
          <p className="mt-5 text-muted-foreground max-w-2xl">
            Desenvolvidos e produzidos pela cadditive. Qualidade profissional a preços acessíveis.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`text-xs px-3 py-1.5 rounded-full border ${category === c ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`shop-filter-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="container-x py-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => (
          <Link key={p.product_id} to={`/loja/${p.product_id}`} data-testid={`shop-product-${p.sku}`} className="group border border-border bg-card overflow-hidden">
            <div className="aspect-square bg-secondary overflow-hidden relative">
              {p.images?.[0] ? (
                <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground mono text-xs uppercase">sem imagem</div>
              )}
              {p.featured && <span className="absolute top-3 left-3 mono text-[10px] uppercase px-2 py-1 bg-background/90 border border-border">destaque</span>}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="mono text-[10px] uppercase text-muted-foreground">{p.sku}</p>
                <p className="mono text-[10px] uppercase text-muted-foreground">{p.material}</p>
              </div>
              <h3 className="font-semibold mt-2">{p.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.short_description}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-bold text-lg">{formatEUR(p.price)}</span>
                <span className="text-brand text-xs inline-flex items-center gap-1">Ver <ArrowRight size={12} /></span>
              </div>
            </div>
          </Link>
        ))}
        {filtered.length === 0 && <div className="col-span-full py-16 text-center tech-label">sem produtos disponíveis</div>}
      </div>
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [selVariant, setSelVariant] = useState({});
  const [custom, setCustom] = useState({});
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      const defaults = {};
      r.data.variants?.forEach((v) => { defaults[v.name] = v.options[0]; });
      setSelVariant(defaults);
    }).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="container-x py-20 tech-label">a carregar…</div>;
  if (!p) return <div className="container-x py-20"><p className="tech-label">produto não encontrado</p></div>;

  const extraPrice = Object.values(custom).reduce((s, c) => s + (c?.price_add || 0), 0);
  const finalUnit = p.price + extraPrice;

  function addToCart() {
    // validate required customs
    for (const opt of p.custom_options || []) {
      if (opt.required && !custom[opt.key]?.value) {
        toast.error(`Campo obrigatório: ${opt.label}`);
        return;
      }
    }
    add({
      product_id: p.product_id, sku: p.sku, name: p.name, unit_price: p.price,
      image: p.images?.[0], variant: selVariant, customizations: custom, quantity: qty,
    });
    toast.success("Produto adicionado ao carrinho");
  }

  return (
    <div className="anim-in container-x py-12">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="crosshair-corners relative aspect-square bg-secondary border border-border overflow-hidden">
          {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
          <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest bg-background/80 px-2 py-1 border border-border">{p.sku}</div>
        </div>
        <div>
          <p className="tech-label mb-2">{p.category}</p>
          <h1 className="text-4xl font-bold tracking-tighter">{p.name}</h1>
          <p className="mt-4 text-muted-foreground">{p.short_description}</p>
          <div className="mt-6 text-3xl font-bold">{formatEUR(finalUnit)}</div>
          <p className="tech-label mt-1">IVA incluído · Portugal</p>

          {p.variants?.map((v) => (
            <div key={v.name} className="mt-6">
              <p className="tech-label mb-2">{v.name}</p>
              <div className="flex flex-wrap gap-2">
                {v.options.map((opt) => (
                  <button key={opt} onClick={() => setSelVariant((s) => ({ ...s, [v.name]: opt }))} data-testid={`product-variant-${v.name}-${opt}`}
                    className={`px-3 py-1.5 text-sm border ${selVariant[v.name] === opt ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {p.custom_options?.map((opt) => (
            <div key={opt.key} className="mt-6">
              <p className="tech-label mb-2">
                {opt.label}
                {opt.price_add > 0 && <span className="text-brand normal-case ml-2">+{formatEUR(opt.price_add)}</span>}
              </p>
              {opt.type === "text" ? (
                <input
                  type="text" maxLength={opt.max_length || 40}
                  placeholder={opt.label}
                  data-testid={`product-custom-${opt.key}`}
                  className="w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-brand"
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustom((c) => ({ ...c, [opt.key]: v ? { value: v, label: opt.label, price_add: opt.price_add } : undefined }));
                  }}
                />
              ) : null}
            </div>
          ))}

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2" data-testid="product-qty-minus">−</button>
              <span className="w-10 text-center mono" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2" data-testid="product-qty-plus">+</button>
            </div>
            <button onClick={addToCart} className="btn-primary flex-1 justify-center" data-testid="product-add-cart">
              <ShoppingCart size={16} /> Adicionar ao carrinho
            </button>
          </div>

          <div className="mt-10 border-t border-border pt-6">
            <p className="text-sm leading-relaxed">{p.description}</p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <Spec label="Material" value={p.material} />
              <Spec label="Cor" value={p.color} />
              <Spec label="Peso" value={p.weight_g ? `${p.weight_g} g` : null} />
              <Spec label="Prazo" value={`${p.lead_time_days} dias`} />
              <Spec label="Stock" value={p.made_to_order ? "Produção por encomenda" : `${p.stock} unidades`} />
              <Spec label="SKU" value={p.sku} mono />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Spec = ({ label, value, mono }) => value ? (
  <div className="flex justify-between border-b border-border pb-2">
    <span className="tech-label">{label}</span>
    <span className={mono ? "mono" : "font-medium"}>{value}</span>
  </div>
) : null;
